import type {
  CoinDetailSnapshot,
  DexSnapshot,
  FundamentalsSnapshot,
} from '../types/market';
import { percentChange } from '../lib/number';
import { getDexSnapshot } from './dex/dexScreener';
import { getBinanceDerivatives, getBinanceTicker } from './exchanges/binance';
import { getBybitTicker } from './exchanges/bybit';
import { getOkxTicker } from './exchanges/okx';
import { getCoinGeckoFundamentals } from './fundamentals/coinGecko';

const UNAVAILABLE_DEX: DexSnapshot = {
  pairCount: 0,
  identityConfidence: 'unavailable',
  source: 'DEX data unavailable',
};

function emptyFundamentals(baseAsset: string): FundamentalsSnapshot {
  return {
    symbol: baseAsset,
    source: 'Fundamentals unavailable',
    confidence: 'unavailable',
    updatedAt: Date.now(),
  };
}

function confidenceForVenue(kind: 'spot' | 'futures' | 'dex', dex: DexSnapshot): 'high' | 'medium' | 'low' {
  if (kind === 'dex') return dex.identityConfidence === 'verified-contract' ? 'high' : 'low';
  return kind === 'spot' ? 'high' : 'medium';
}

export async function getCoinDetail(symbol: string): Promise<CoinDetailSnapshot> {
  const normalized = symbol.toUpperCase();
  const baseAsset = normalized.endsWith('USDT') ? normalized.slice(0, -4) : normalized;

  const [spotResult, futuresResult, derivativesResult, fundamentalsResult, bybitResult, okxResult] = await Promise.allSettled([
    getBinanceTicker(normalized, 'spot'),
    getBinanceTicker(normalized, 'futures'),
    getBinanceDerivatives(normalized),
    getCoinGeckoFundamentals(baseAsset),
    getBybitTicker(normalized),
    getOkxTicker(baseAsset),
  ]);

  const spot = spotResult.status === 'fulfilled' ? spotResult.value : undefined;
  const futures = futuresResult.status === 'fulfilled' ? futuresResult.value : undefined;
  const derivatives = derivativesResult.status === 'fulfilled'
    ? derivativesResult.value
    : {
        fundingBehavior: 'unavailable' as const,
        source: 'Derivatives data unavailable',
      };
  const fundamentals = fundamentalsResult.status === 'fulfilled'
    ? fundamentalsResult.value
    : emptyFundamentals(baseAsset);
  const bybit = bybitResult.status === 'fulfilled' ? bybitResult.value : null;
  const okx = okxResult.status === 'fulfilled' ? okxResult.value : null;

  const dex = await getDexSnapshot(baseAsset, fundamentals.contracts).catch(() => UNAVAILABLE_DEX);

  const externalPrimary = bybit?.price
    ? { price: bybit.price, change24h: bybit.change24h, source: 'Bybit futures' }
    : okx?.price
      ? { price: okx.price, change24h: okx.change24h, source: 'OKX perpetual' }
      : undefined;

  if (!spot && !futures && !externalPrimary) {
    return {
      symbol: normalized,
      baseAsset,
      price: 0,
      change24h: 0,
      fundamentals,
      dex,
      derivatives,
      attribution: [],
      attributionWindow: '24h-turnover',
      attributionConfidence: 'low',
      observedDriver: 'Current-move attribution unavailable',
      observedDriverExplanation:
        'Pilum could not resolve a reliable public spot or futures market. No claim is made about what is driving the current move.',
      updatedAt: Date.now(),
    };
  }

  // Prefer spot, then Binance futures, then an independently resolved external venue.
  const primaryPrice = spot?.price ?? futures?.price ?? externalPrimary!.price;
  const primaryChange24h = spot
    ? percentChange(spot.price, spot.open24h)
    : futures
      ? percentChange(futures.price, futures.open24h)
      : externalPrimary?.change24h ?? 0;
  const spotVolume = spot?.quoteVolume24h;
  const binanceFuturesVolume = derivatives.futuresVolume24h ?? futures?.quoteVolume24h;
  const bybitFuturesVolume = bybit?.quoteVolume24h;
  const futuresVolume = (binanceFuturesVolume ?? 0) + (bybitFuturesVolume ?? 0) || undefined;

  // Symbol-only DEX matches are useful as a lead, but they are not safe to merge into asset totals.
  const verifiedDexVolume = dex.identityConfidence === 'verified-contract' ? dex.volume24h : undefined;
  const totalObservedVolume24h = (spotVolume ?? 0) + (futuresVolume ?? 0) + (verifiedDexVolume ?? 0);

  const rawAttribution = [
    {
      label: 'Binance spot',
      amount: spotVolume,
      confidence: confidenceForVenue('spot', dex),
    },
    {
      label: 'Binance futures',
      amount: binanceFuturesVolume,
      confidence: confidenceForVenue('futures', dex),
    },
    {
      label: 'Bybit futures',
      amount: bybitFuturesVolume,
      confidence: confidenceForVenue('futures', dex),
    },
    {
      label: 'Contract-verified DEX pools',
      amount: verifiedDexVolume,
      confidence: confidenceForVenue('dex', dex),
    },
  ].filter((item): item is { label: string; amount: number; confidence: 'high' | 'medium' | 'low' } =>
    Number.isFinite(item.amount) && (item.amount ?? 0) > 0,
  );

  const attribution = rawAttribution.map((item) => ({
    ...item,
    percentage: totalObservedVolume24h > 0 ? (item.amount / totalObservedVolume24h) * 100 : 0,
  }));

  const futuresShare = attribution
    .filter((item) => item.label.includes('futures'))
    .reduce((sum, item) => sum + item.percentage, 0);
  const spotShare = attribution.find((item) => item.label === 'Binance spot')?.percentage ?? 0;
  const dexShare = attribution.find((item) => item.label === 'Contract-verified DEX pools')?.percentage ?? 0;

  let observedDriver = 'Mixed 24-hour venue activity';
  let observedDriverExplanation =
    'This is a turnover mix for the previous 24 hours, not proof of what caused the current pump or fall. Recent trade flow, open-interest changes, liquidations, structure, and news must be studied separately.';

  if (futuresShare >= 65) {
    observedDriver = '24-hour activity is futures-heavy';
    observedDriverExplanation =
      'Most resolved 24-hour turnover is in perpetual futures. This can increase leverage sensitivity, but it does not by itself prove that futures caused the current move.';
  } else if (spotShare >= 45 && spotShare > futuresShare) {
    observedDriver = '24-hour activity is spot-led';
    observedDriverExplanation =
      'Resolved spot turnover is larger than observed futures turnover. This is broader participation context, not a standalone explanation of the current move.';
  } else if (dexShare >= 20) {
    observedDriver = 'Meaningful contract-verified DEX activity';
    observedDriverExplanation =
      'Contract-resolved DEX pools contribute a meaningful share of observed 24-hour turnover. Pool concentration and current flow still require separate analysis.';
  }

  const attributionConfidence: CoinDetailSnapshot['attributionConfidence'] =
    spot && futures && bybit && (dex.identityConfidence === 'verified-contract' || dex.pairCount === 0)
      ? 'high'
      : spot && futures
        ? 'medium'
        : 'low';

  return {
    symbol: normalized,
    baseAsset,
    price: primaryPrice,
    change24h: primaryChange24h,
    spotVolume24h: spotVolume,
    futuresVolume24h: futuresVolume,
    totalObservedVolume24h,
    fundamentals,
    dex,
    derivatives,
    attribution,
    attributionWindow: '24h-turnover',
    attributionConfidence,
    externalVenues: [
      ...(bybit
        ? [{ exchange: 'Bybit', price: bybit.price, volume24h: bybit.quoteVolume24h, fundingRate: bybit.fundingRate }]
        : []),
      ...(okx ? [{ exchange: 'OKX', price: okx.price, volume24h: okx.quoteVolume24h }] : []),
    ],
    observedDriver,
    observedDriverExplanation,
    updatedAt: Date.now(),
  };
}
